import { Endpoints } from '@constants/endpoints.constant'
import { Methods } from '@constants/methods.constant'
import AuthTokens from '@interfaces/auth.interface'
import { Settings } from '@interfaces/settings.interface'
import { t } from '@lingui/macro'
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import qs from 'qs'
import { toast } from 'react-toastify'
import { localStorage } from './local-storage.service'
import { StorageKeys } from '@constants/storage-keys.constant'

const MAX_RETRY_ATTEMPTS = 3 // Define the maximum number of retry attempts

interface RequestApiProps {
  endpoint: string
  method: string
  query?: any
  headers?: Record<string, string>
  data?: any
  messageOnSuccess?: string
  onError?: () => void
  responseType?: 'json' | 'blob' | 'text'
}

interface ApiResponse<T = any> extends AxiosResponse {
  data: T
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const retryCount = error?.config?.retryCount || 0 // Get the current retry count or initialize to 0

    // Check if the maximum retry attempts have been reached
    if (retryCount >= MAX_RETRY_ATTEMPTS) {
      return Promise.reject(error) // Reject the request if maximum retries exceeded
    }

    // Increment the retry count and update the request config
    const newRetryCount = retryCount + 1
    error.config.retryCount = newRetryCount

    // Retry the request with updated config
    return retryRequest(error)
  },
)

const retryRequest = async (error) => {
  const tokens = localStorage.getItem(StorageKeys.AUTH_TOKENS) as AuthTokens
  const initialRequestEndpoint = error.config?.url.replace('/api', '')
  const initialRequestMethod = error.config?.method.toUpperCase()
  const isInitialRequestSignout = initialRequestEndpoint === Endpoints.AUTH && initialRequestMethod === Methods.DELETE
  const isInitialRequestRefresh = initialRequestEndpoint === Endpoints.TOKENS && initialRequestMethod === Methods.GET

  // If 401 response is received and the token has expired, try to refresh the token and retry the request
  if (!isInitialRequestRefresh && error instanceof AxiosError && error?.response?.status === 401 && tokens?.accessToken && tokens?.refreshToken) {
    // Refresh the token
    const requestOptions = {
      method: Methods.GET,
      url: '/api' + Endpoints.TOKENS,
      headers: {
        Authorization: `Bearer ${tokens?.refreshToken}`,
        'Content-Type': 'application/json',
      },
    }
    const refreshResponse = await axios(requestOptions)
    localStorage.setItem(StorageKeys.AUTH_TOKENS, refreshResponse.data)
    const { accessToken, refreshToken } = localStorage.getItem(StorageKeys.AUTH_TOKENS) as AuthTokens
    // The refreshToken is used to call the /auth DELETE endpoint, so we need to use the accessToken to retry the request
    const token = isInitialRequestSignout ? refreshToken : accessToken
    // Retrying the request
    return await axios.request({
      ...error.config,
      headers: { Authorization: `Bearer ${token}` },
    } as AxiosRequestConfig)
  } else if (error instanceof AxiosError && error?.response?.status === 401) {
    localStorage.removeItem(StorageKeys.AUTH_TOKENS)
    window.location.href = '/signin'
    window.location.reload()
  }
  return Promise.reject(error)
}

export async function requestApi<T = any>({
  endpoint,
  method,
  query,
  headers,
  data,
  messageOnSuccess,
  onError,
  responseType = 'json',
}: RequestApiProps): Promise<ApiResponse<T>> {
  const tokens = localStorage.getItem(StorageKeys.AUTH_TOKENS) as AuthTokens
  const language = (localStorage.getItem(StorageKeys.SETTINGS) as Settings)?.language || navigator.language.split('-')[0] || 'en'
  const customHeaders = {
    'x-lang': language,
    'Content-Type': 'application/json',
    ...headers,
  }

  if (tokens) {
    customHeaders['Authorization'] = `Bearer ${tokens?.accessToken || ''}`
  }

  try {
    const res = await axios<T>({
      method,
      url: `/api${endpoint}${query ? `?${qs.stringify(query)}` : ''}`,
      data,
      headers: customHeaders,
      responseType,
    })
    if (messageOnSuccess) {
      toast.success(messageOnSuccess)
    }
    return res
  } catch (err) {
    if (err instanceof AxiosError) {
      if (err?.response?.data?.message) {
        toast.error(err?.response?.data?.message)
      } else {
        toast.error(t({ message: 'Something went wrong' }))
      }
    }
    onError && onError()
    throw err
  }
}

export default requestApi
